import { useRef, useState } from 'react';
import { cn } from 'cn';
import type { GlossedWord } from './learningApi';

/**
 * The word-meaning layer over an Arabic passage.
 *
 * Tap or click any word for its gloss. The rules that are not cosmetic:
 *
 * - **State is carried by shape, not colour.** A word with no gloss yet has a
 *   dashed underline; a glossed one is plain. Colour-blind readers and the
 *   High Contrast theme both need that, and "which words can I tap" is the
 *   first thing a learner asks.
 * - **Root letters are spaced, never joined** (ت ق و). Joined, they stop
 *   being a root and become a word.
 * - **Tashkeel intact, never bold.** The chip shows the word the khutbah
 *   actually used.
 * - **Keyboard operable.** Each glossed word is a real button, so Tab reaches
 *   it and Enter opens it; Escape closes.
 */
export function WordLayer({
  words,
  lang,
  dir,
  scriptClass,
  translationScript,
  translationDir,
}: {
  words: readonly GlossedWord[];
  lang: string;
  dir: 'ltr' | 'rtl';
  scriptClass: string;
  translationScript: string;
  translationDir: 'ltr' | 'rtl';
}) {
  const [active, setActive] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  if (words.length === 0) {
    return null;
  }

  const open = active === null ? null : (words[active] ?? null);

  const glossedCount = words.filter((w) => w.state === 'glossed').length;
  // Which words to draw. Only the glossed ones by default.
  //
  // Coverage is 6% today -- 898 of 959 words in the first real khutbah have no
  // meaning yet -- so drawing every word made a row where 93% of the chips
  // were dead. A reader taps one, nothing happens, and concludes the feature
  // is broken. That was reported, and it was a fair reading: the heading said
  // "Words with a meaning" while the row listed every word in the passage.
  //
  // `showAll` keeps word-by-word reading available, which is the point of the
  // layer, but it is no longer the default while coverage is this thin.
  const shown = showAll ? words : words.filter((w) => w.state === 'glossed');

  return (
    <div
      ref={containerRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && active !== null) {
          event.stopPropagation();
          setActive(null);
        }
      }}
      className="relative mt-3"
    >
      {/*
        Labelled, because the chips are NOT the passage. Glossing is per word
        and coverage is partial — most khutbahs have a few dozen glossed words
        out of hundreds — so an unlabelled row of Arabic beneath the passage
        reads as the passage repeated and cut short. It is a word list.
      */}
      <div className="mb-1 flex flex-wrap items-baseline gap-x-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {glossedCount === 0
            ? 'No word meanings for this passage yet'
            : showAll
              ? `Word by word · ${glossedCount} of ${words.length} have a meaning`
              : `Words with a meaning · ${glossedCount}`}
        </p>
        {/* Only worth offering when it would actually change what is drawn. */}
        {glossedCount < words.length ? (
          <button
            type="button"
            onClick={() => {
              setShowAll((was) => !was);
              setActive(null);
            }}
            className="text-[11px] underline underline-offset-2 text-muted-foreground hover:text-foreground"
          >
            {showAll ? 'Show only words with a meaning' : `Show all ${words.length} words`}
          </button>
        ) : null}
      </div>
      <p
        lang={lang}
        dir={dir}
        className={cn(scriptClass, 'flex flex-wrap gap-x-2 gap-y-1 font-normal')}
      >
        {shown.map((word) => {
          const glossed = word.state === 'glossed';
          // Index into `words`, not into `shown`: `open` reads from `words`,
          // and filtering the row would otherwise open the wrong word's
          // meaning -- an off-by-filter that shows a reader the wrong gloss.
          const index = words.indexOf(word);
          return (
            <button
              key={`${word.ordinal}-${word.surface}`}
              type="button"
              // Not a button when there is nothing to show: a control that
              // opens an empty popover is worse than plain text.
              disabled={!glossed}
              aria-expanded={glossed ? active === index : undefined}
              onClick={() => setActive(active === index ? null : index)}
              className={cn(
                'min-h-11 rounded-sm px-0.5 text-start',
                'outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                glossed
                  ? // A solid underline, not just a hover: on a touch screen
                    // there is no hover, so hover was the only affordance and
                    // a reader had no way to tell which words would respond.
                    'cursor-pointer underline decoration-dotted decoration-2'
                    + ' underline-offset-4 decoration-primary/70 hover:bg-accent'
                  : // Dashed and dimmed, so "no gloss yet" is visible without
                    // colour and clearly not the same kind of thing.
                    'cursor-default text-muted-foreground decoration-dashed'
                    + ' underline underline-offset-4 decoration-muted-foreground/40',
                active === index && 'bg-accent',
              )}
            >
              {word.surface}
            </button>
          );
        })}
      </p>

      {open !== null ? (
        <div
          role="dialog"
          aria-label={`Meaning of ${open.surface}`}
          className="absolute z-40 mt-1 w-[300px] rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-2 start-0"
        >
          <p lang="ar" dir="rtl" className={cn(scriptClass, 'text-2xl font-normal')}>
            {open.surface}
          </p>

          {open.transliteration ? (
            <p className="mt-0.5 text-[13px] italic text-muted-foreground">
              {open.transliteration}
            </p>
          ) : null}

          <dl className="mt-2 flex flex-col gap-1.5 text-[13px]">
            {open.meaning ? (
              <div>
                <dt className="sr-only">Meaning</dt>
                <dd
                  lang={open.meaning ? undefined : undefined}
                  dir={translationDir}
                  className={cn(translationScript, 'text-foreground')}
                >
                  {open.meaning}
                </dd>
              </div>
            ) : null}

            {open.root ? (
              <div className="flex gap-2">
                <dt className="text-muted-foreground">Root</dt>
                {/* Spaced in the data and kept spaced here, with extra
                    tracking so the letters never read as a word. */}
                <dd lang="ar" dir="rtl" className={cn(scriptClass, 'tracking-[0.3em]')}>
                  {open.root}
                </dd>
              </div>
            ) : null}

            {open.lemma ? (
              <div className="flex gap-2">
                <dt className="text-muted-foreground">Dictionary form</dt>
                <dd lang="ar" dir="rtl" className={scriptClass}>
                  {open.lemma}
                </dd>
              </div>
            ) : null}

            {open.partOfSpeech ? (
              <div className="flex gap-2">
                <dt className="text-muted-foreground">Part of speech</dt>
                <dd>{open.partOfSpeech}</dd>
              </div>
            ) : null}
          </dl>

          {open.note ? (
            <p className="mt-2 text-[12px] leading-[17px] text-muted-foreground">
              {open.note}
            </p>
          ) : null}

          {/* Learning content is AI-generated until a reviewer approves it,
              and a learner is told which this is. */}
          <p className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">
            {open.reviewed
              ? 'Checked by a reviewer.'
              : 'Not yet checked by a reviewer.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
